import React, { useEffect, useRef } from 'react';
import { UpdateWorkspace } from '../../../../shared/types/workspaceTypes';
import styled from 'styled-components';
import { FlexRow } from '../../../../shared/FlexRow';
import { FlexCol } from '../../../../shared/FlexCol';
import { GuidePreview } from '../GuidePreview';

interface Props {
  data: UpdateWorkspace;
  handleClose?: () => void;
}

export const GuidePopup: React.FC<Props> = ({ data, handleClose }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || !handleClose) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndY = e.changedTouches[0].clientY;
      const swipeDistance = touchEndY - touchStartY.current;

      if (swipeDistance > 10) {
        handleClose();
      }
    };

    slider.addEventListener('touchstart', handleTouchStart);
    slider.addEventListener('touchend', handleTouchEnd);

    return () => {
      slider.removeEventListener('touchstart', handleTouchStart);
      slider.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleClose]);

  return (
    <Container>
      <ContentContainer>
        <Header>
          <SliderBar ref={sliderRef} />
          <FlexRow width={'100%'} justify={'space-between'} align={'center'} style={{ height: 'fit-content', padding: '0.6rem 1.6rem' }}>
            <button onClick={handleClose}>
              <CloseIcon />
            </button>
            <ChevronIcon />
            <FlexCol width={'fit-content'} align={'center'} gap={'0.2rem'}>
              <TitleText>인터뷰알리미 캔디데이트</TitleText>
              <SubtitleText>webview.candidate.im</SubtitleText>
            </FlexCol>
            <ChevronIcon rotate />
            <button>
              <img src='/images/workspace-wizard/kakao.svg' alt='chevron' style={{ width: '2rem', height: '2rem' }} />
            </button>
          </FlexRow>
        </Header>
        <GuidePreview data={data} />
      </ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: calc(100% - 5.6rem);
  background-color: #00000033;
  padding-top: 2rem;

  position: absolute;
  top: 5.6rem;
  left: 0;
  z-index: 5000;
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--background-2);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const Header = styled.div`
  width: 100%;
  padding: 0.6rem 0 0.4rem;
  border-bottom: 1px solid var(--border-color);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 0.4rem;
`;

const SliderBar = styled.div`
  width: 4rem;
  height: 0.4rem;
  background-color: var(--background-4);
  border-radius: 20px;
  cursor: grab;
  touch-action: pan-y;
`;

const CloseIcon = styled.div`
  width: 2rem;
  height: 2rem;
  mask-image: url('/images/common/close.svg');
  mask-size: cover;
  mask-repeat: no-repeat;
  background-color: var(--content-color-primary);
`;

const ChevronIcon = styled.div<{ rotate?: boolean }>`
  width: 2rem;
  height: 2rem;
  mask-image: url('/images/common/chevron.svg');
  mask-size: cover;
  mask-repeat: no-repeat;
  background-color: var(--content-color-disabled);
  transform: ${({ rotate }) => (rotate ? 'rotate(180deg)' : 'none')};
`;

const TitleText = styled.div`
  color: var(--content-color-primary);
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.8rem;
`;

const SubtitleText = styled.div`
  color: var(--placeholder);
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1.8rem;
`;
